/**
 * GPU host create / edit modal (shadcn Dialog + react-hook-form + zod).
 *
 * One form covers every column on omega_engine.registered_hosts that
 * admins should touch, including the bridge-transport columns
 * (auth_scheme, auth_secret_ref, r2_output_prefix) — the admin UI is
 * the operational surface for the control plane.
 *
 * On create:  POST /admin/gpu/hosts
 * On edit:    PUT  /admin/gpu/hosts/:id (only changed fields)
 *
 * `auth_token` (plaintext, optional) is only available on create — the
 * server stores its sha256 and the plaintext is never readable later.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, X } from 'lucide-react';
import { useEffect, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';

import {
  createGpuHost,
  updateGpuHost,
  type AuthScheme,
  type CreateGpuHostInput,
  type GpuHost,
  type HostStatus,
  type UpdateGpuHostInput,
} from '@/services/api/gpu-hosts.js';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STATUS_OPTIONS: HostStatus[] = ['active', 'drain', 'disabled', 'maintenance'];
const AUTH_SCHEMES: { value: AuthScheme; label: string; help: string }[] = [
  {
    value: 'service_token',
    label: 'service_token',
    help: 'Legacy. Gateway sends a shared bearer token in Authorization header.',
  },
  {
    value: 'cf_access',
    label: 'cf_access',
    help: 'Cloudflare Access service token. Gateway sends CF-Access-Client-Id/Secret. Use auth_secret_ref to pick which env-bound token pair.',
  },
  {
    value: 'mtls',
    label: 'mtls',
    help: 'Mutual TLS. Uses mtls_cert_id to look up the client cert bundle.',
  },
];

const CAPABILITY_PRESETS = ['comfyui', 'vllm', 'sdxl', 'flux', 'wan', 'qwen-image', 'whisper'];

const FormSchema = z.object({
  host_url: z.string().min(1, 'Required').max(256),
  datacenter: z.string().min(1, 'Required').max(64),
  capability_set: z.array(z.string().min(1).max(64)).min(1, 'At least one capability'),
  weight: z.number().int().min(1).max(100),
  status: z.enum(['active', 'drain', 'disabled', 'maintenance']),
  auth_scheme: z.enum(['service_token', 'cf_access', 'mtls']),
  auth_secret_ref: z.string().max(64).nullable().optional(),
  mtls_cert_id: z.string().max(64).nullable().optional(),
  r2_output_prefix: z.string().max(256).nullable().optional(),
  auth_token: z.string().min(16).max(256).optional().or(z.literal('')),
  notes: z.string().max(1024).nullable().optional(),
});
type FormShape = z.infer<typeof FormSchema>;

interface Props {
  mode: 'create' | 'edit';
  host?: GpuHost;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyToNull = (v: string | null | undefined): string | null => {
  if (v == null) return null;
  const s = v.trim();
  return s === '' ? null : s;
};

function hostToFormValues(host: GpuHost | null | undefined): FormShape {
  if (!host) {
    return {
      host_url: '',
      datacenter: '',
      capability_set: [],
      weight: 1,
      status: 'active',
      auth_scheme: 'cf_access',
      auth_secret_ref: null,
      mtls_cert_id: null,
      r2_output_prefix: null,
      auth_token: '',
      notes: null,
    };
  }
  return {
    host_url: host.host_url,
    datacenter: host.datacenter,
    capability_set: host.capability_set,
    weight: host.weight,
    status: host.status,
    auth_scheme: host.auth_scheme,
    auth_secret_ref: host.auth_secret_ref,
    mtls_cert_id: host.mtls_cert_id,
    r2_output_prefix: host.r2_output_prefix,
    auth_token: '',
    notes: host.notes,
  };
}

export function GpuHostFormModal({ mode, host, onClose, onSuccess }: Props): JSX.Element {
  const form = useForm<FormShape>({
    resolver: zodResolver(FormSchema),
    defaultValues: hostToFormValues(host ?? null),
  });

  useEffect(() => {
    form.reset(hostToFormValues(host ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host?.id]);

  const authScheme = form.watch('auth_scheme');
  const capabilities = form.watch('capability_set');
  const authSchemeHelp = AUTH_SCHEMES.find((o) => o.value === authScheme)?.help ?? '';

  const createMut = useMutation({
    mutationFn: (input: CreateGpuHostInput) => createGpuHost(input),
    onSuccess: () => {
      toast.success('Host created');
      onSuccess();
    },
    onError: (e: unknown) => toast.error(`Create failed: ${(e as Error).message}`),
  });

  const updateMut = useMutation({
    mutationFn: (input: UpdateGpuHostInput) => {
      if (!host) throw new Error('no host');
      return updateGpuHost(host.id, input);
    },
    onSuccess: () => {
      toast.success('Host updated');
      onSuccess();
    },
    onError: (e: unknown) => toast.error(`Update failed: ${(e as Error).message}`),
  });

  const submitting = createMut.isPending || updateMut.isPending;

  const onSubmit = form.handleSubmit((v) => {
    if (mode === 'create') {
      const payload: CreateGpuHostInput = {
        host_url: v.host_url.trim(),
        datacenter: v.datacenter.trim(),
        capability_set: v.capability_set,
        weight: v.weight,
        status: v.status,
        auth_scheme: v.auth_scheme,
        auth_secret_ref: emptyToNull(v.auth_secret_ref),
        mtls_cert_id: emptyToNull(v.mtls_cert_id),
        r2_output_prefix: emptyToNull(v.r2_output_prefix),
        notes: emptyToNull(v.notes),
      };
      if (v.auth_token && v.auth_token.length >= 16) {
        payload.auth_token = v.auth_token;
      }
      createMut.mutate(payload);
    } else {
      const payload: UpdateGpuHostInput = {
        host_url: v.host_url.trim(),
        datacenter: v.datacenter.trim(),
        capability_set: v.capability_set,
        weight: v.weight,
        status: v.status,
        auth_scheme: v.auth_scheme,
        auth_secret_ref: emptyToNull(v.auth_secret_ref),
        mtls_cert_id: emptyToNull(v.mtls_cert_id),
        r2_output_prefix: emptyToNull(v.r2_output_prefix),
        notes: emptyToNull(v.notes),
      };
      updateMut.mutate(payload);
    }
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add GPU host' : `Edit · ${host?.id ?? ''}`}
          </DialogTitle>
          <DialogDescription>
            All bridge-transport fields are first-class. Conditional rendering by auth scheme.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={onSubmit}
          className="space-y-4"
          id="gpu-host-form"
        >
          {/* host_url */}
          <FormField label="Host URL" error={form.formState.errors.host_url?.message}>
            <Input
              {...form.register('host_url')}
              placeholder="https://omega-bridge-a100.tacosai.com/v1/comfyui/30-cuda0"
            />
            <p className="text-xs text-muted-foreground">
              Full URL the gateway uses to call this GPU. For CF Access hosts include the bridge prefix.
            </p>
          </FormField>

          {/* datacenter + weight + status */}
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Datacenter" error={form.formState.errors.datacenter?.message}>
              <Input {...form.register('datacenter')} placeholder="a100 | office | …" />
            </FormField>
            <FormField label="Weight" error={form.formState.errors.weight?.message}>
              <Input type="number" min={1} max={100} {...form.register('weight', { valueAsNumber: true })} />
            </FormField>
            <FormField label="Status">
              <SelectField
                value={form.watch('status')}
                onChange={(v) => form.setValue('status', v as HostStatus, { shouldDirty: true })}
                options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
              />
            </FormField>
          </div>

          {/* capabilities */}
          <FormField label="Capabilities" error={form.formState.errors.capability_set?.message as string | undefined}>
            <div className="flex flex-wrap gap-1.5 rounded-md border bg-background p-2">
              {CAPABILITY_PRESETS.map((c) => {
                const on = capabilities.includes(c);
                return (
                  <button
                    type="button"
                    key={c}
                    onClick={() => {
                      const next = on
                        ? capabilities.filter((x) => x !== c)
                        : [...capabilities, c];
                      form.setValue('capability_set', next, { shouldDirty: true, shouldValidate: true });
                    }}
                    className="focus:outline-none"
                  >
                    <Badge
                      variant={on ? 'default' : 'outline'}
                      className={on ? '' : 'opacity-60 hover:opacity-100'}
                    >
                      {c}
                      {on ? <X className="ml-1 h-3 w-3" /> : null}
                    </Badge>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Routing tags the gateway matches against job requirements. Click a tag to toggle.
            </p>
          </FormField>

          {/* Bridge transport divider */}
          <div className="pt-2">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Bridge transport
            </h3>
          </div>

          {/* auth_scheme */}
          <FormField label="Auth scheme">
            <SelectField
              value={authScheme}
              onChange={(v) => form.setValue('auth_scheme', v as AuthScheme, { shouldDirty: true })}
              options={AUTH_SCHEMES.map((o) => ({ value: o.value, label: o.label }))}
            />
            <p className="text-xs text-muted-foreground">{authSchemeHelp}</p>
          </FormField>

          {authScheme === 'cf_access' ? (
            <FormField label="auth_secret_ref">
              <Input
                {...form.register('auth_secret_ref')}
                placeholder="tacosai-gpu-gateway"
              />
              <p className="text-xs text-muted-foreground">
                Logical name for the CF Access token pair. Gateway resolves to env{' '}
                <code className="rounded bg-muted px-1 py-0.5">CF_ACCESS_&lt;REF&gt;_ID</code>{' '}
                and{' '}
                <code className="rounded bg-muted px-1 py-0.5">CF_ACCESS_&lt;REF&gt;_SECRET</code>.
              </p>
            </FormField>
          ) : null}

          {authScheme === 'mtls' ? (
            <FormField label="mtls_cert_id">
              <Input {...form.register('mtls_cert_id')} placeholder="gateway-prod-2026q2" />
              <p className="text-xs text-muted-foreground">
                Identifier for the client cert bundle. Gateway maps this to actual cert/key.
              </p>
            </FormField>
          ) : null}

          <FormField label="R2 output prefix">
            <Input
              {...form.register('r2_output_prefix')}
              placeholder="gpu-outputs/30-cuda0/"
            />
            <p className="text-xs text-muted-foreground">
              Where the output-uploader sidecar writes files. Blank for hosts that produce no files (vLLM).
            </p>
          </FormField>

          {mode === 'create' ? (
            <FormField
              label="auth_token (plaintext, optional)"
              error={form.formState.errors.auth_token?.message}
            >
              <Input
                type="password"
                {...form.register('auth_token')}
                placeholder="Inbound-callback shared secret (16–256 chars; sha256-hashed at rest)"
              />
              <p className="text-xs text-muted-foreground">
                Stored as <code className="rounded bg-muted px-1 py-0.5">sha256(auth_token)</code>.
                Used by the gateway to validate inbound webhook callbacks from the GPU host.
                Plaintext is never returned after this create.
              </p>
            </FormField>
          ) : null}

          <FormField label="Notes">
            <textarea
              {...form.register('notes')}
              rows={3}
              placeholder="Free-form: physical location, GPU model, owner, etc."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </FormField>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form="gpu-host-form" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {mode === 'create' ? 'Creating' : 'Saving'}
              </>
            ) : mode === 'create' ? (
              'Create'
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function SelectField({
  value,
  onChange,
  options,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
